import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { Form, Input } from 'semantic-ui-react'
import { useForm } from 'react-hook-form'
import {
    AppPrimaryButton,
    AppSecondaryButton,
    FormTextFormField,
    FormDropdownField,
    FormDateField,
    FormTextAreaFormField,
} from 'elements'
import {
    Warning,
    MagnifyingGlass,
    MapPin,
    PencilSimple,
    ArrowCounterClockwise,
    Check,
    CaretUp,
    CaretDown,
    ClipboardText,
} from 'phosphor-react'
import { LoaderBar } from 'components'
import { GetCustomerList, GetExternalCustomerList } from 'hooks'
import { useNotification } from 'hooks/toast'
import {
    SOURCE_TABS,
    PRODUCT_OPTIONS,
    COMPLAINT_TYPE_OPTIONS,
    SEVERITY_OPTIONS,
    SOURCE_BADGE_LABELS,
} from './constants'
import {
    buildCustomerLists,
    filterCustomers,
    formatLocationLine,
    getInitials,
    isHighSeverity,
    isToday,
    formatComplaintDate,
} from './utils'
import { isClaimRowFilled } from './customer-claim-inspection-utils'

const PageWrap = styled.div`
    margin: -1rem;
    min-height: calc(100vh - 55px);
    background: #eef1f5;
    font-family: inherit;
`

const TopHeader = styled.div`
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};
    padding: 0.5rem 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.5rem;
`

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`

const HeaderTitle = styled.h1`
    margin: 0;
    font-size: 1.1rem;
    font-weight: 700;
    line-height: 1.2;
`

const HeaderSubtitle = styled.p`
    margin: 0.15rem 0 0;
    font-size: 0.72rem;
    opacity: 0.75;
    line-height: 1.2;
`

const StatsRow = styled.div`
    display: flex;
    gap: 0.65rem;
    flex-wrap: wrap;
    align-items: stretch;
`

const StatBox = styled.div`
    border: 1px solid rgba(255, 255, 255, 0.35);
    border-radius: 5px;
    padding: 0.35rem 0.75rem;
    text-align: center;
    min-width: 88px;
    min-height: 48px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.08);
    box-sizing: border-box;
`

const StatValue = styled.div`
    font-size: 1.25rem;
    font-weight: 700;
    line-height: 1;
    color: ${props => (props.$alwaysRed || props.$danger ? '#f87171' : '#fff')};
`

const StatLabel = styled.div`
    font-size: 0.58rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    opacity: 0.9;
    margin-top: 0.15rem;
    white-space: nowrap;
    line-height: 1;
`

const BodyLayout = styled.div`
    display: flex;
    align-items: stretch;
    min-height: calc(100vh - 110px);
    overflow: hidden;

    @media (max-width: 991px) {
        flex-direction: column;
        overflow: visible;
    }
`

const Sidebar = styled.aside`
    width: 280px;
    flex-shrink: 0;
    background: #fff;
    border-right: 1px solid #dde3ea;
    display: flex;
    flex-direction: column;
    height: calc(100vh - 108px);
    min-height: 400px;
    overflow: hidden;

    @media (max-width: 991px) {
        width: 100%;
        border-right: none;
        border-bottom: 1px solid #dde3ea;
        height: 320px;
        min-height: 320px;
    }
`

const SourceTabs = styled.div`
    display: flex;
    border-bottom: 1px solid #e8ecf0;
`

const SourceTab = styled.button`
    flex: 1;
    border: none;
    background: transparent;
    padding: 0.65rem 0.35rem;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    cursor: pointer;
    color: ${props => (props.$active ? props.$activeColor || '#1a2b4b' : '#6b7280')};
    border-bottom: 3px solid
        ${props => (props.$active ? props.$underlineColor || '#e11d8f' : 'transparent')};
    transition: color 0.15s ease;

    &:hover {
        color: #1a2b4b;
    }
`

const TabCount = styled.span`
    display: block;
    font-size: 0.85rem;
    margin-top: 0.15rem;
`

const SearchWrap = styled.div`
    padding: 0.5rem 0.75rem;
    position: relative;
    flex-shrink: 0;
    width: 100%;
    box-sizing: border-box;

    .ui.input {
        width: 100% !important;
    }

    input {
        width: 100% !important;
        box-sizing: border-box !important;
        padding: 0.55rem 0.65rem 0.55rem 2.1rem !important;
        border: 1px solid #d1d5db !important;
        border-radius: 6px !important;
        font-size: 0.85rem !important;
        height: 38px !important;
        min-height: 38px !important;
    }
`

const SearchIcon = styled.span`
    position: absolute;
    left: 1.25rem;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
    display: flex;
    pointer-events: none;
    z-index: 1;
`

const ListMeta = styled.div`
    padding: 0 0.75rem 0.35rem;
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    color: #9ca3af;
`

const LocationList = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 0 0.35rem 0.5rem;

    &::-webkit-scrollbar {
        width: 7px;
    }

    &::-webkit-scrollbar-track {
        background: #f1f3f5;
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: #b8c4d0;
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: #9aa8b8;
    }
`

const LocationItem = styled.button`
    width: 100%;
    border: none;
    text-align: left;
    display: flex;
    align-items: center;
    gap: 0.65rem;
    padding: 0.55rem 0.65rem;
    margin-bottom: 0.2rem;
    border-radius: 6px;
    cursor: pointer;
    background: ${props => (props.$selected ? props.$selectedBg : 'transparent')};
    color: ${props => (props.$selected ? '#fff' : '#1f2937')};

    &:hover {
        background: ${props => (props.$selected ? props.$selectedBg : '#f3f6f9')};
    }
`

const Avatar = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.7rem;
    font-weight: 700;
    background: ${props => (props.$selected ? 'rgba(255,255,255,0.25)' : props.$bg || '#dbeafe')};
    color: ${props => (props.$selected ? '#fff' : '#1e40af')};
`

const LocationName = styled.div`
    font-size: 0.78rem;
    font-weight: 700;
    line-height: 1.25;
`

const LocationSub = styled.div`
    font-size: 0.7rem;
    opacity: ${props => (props.$selected ? 0.9 : 0.65)};
    margin-top: 0.1rem;
`

const MainArea = styled.main`
    flex: 1;
    padding: 1rem;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
`

const Card = styled.div`
    background: #fff;
    border-radius: 8px;
    border: 1px solid #e5e9ef;
    overflow: hidden;
`

const FormCard = styled(Card)`
    overflow: visible;
`

const FIELD_HEIGHT = '38px'

const PlaceholderCard = styled(Card)`
    padding: 0;
    min-height: 88px;
`

const PlaceholderInner = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 88px;
    padding: 1.25rem 1.5rem;
    gap: 0.5rem;
    text-align: center;
`

const PlaceholderIconWrap = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    color: #b8c4d0;
`

const PlaceholderText = styled.p`
    margin: 0;
    max-width: 420px;
    text-align: center;
    font-size: 0.88rem;
    color: #9ca3af;
    line-height: 1.45;
`

const CustomerHeader = styled.div`
    padding: 1rem 1.25rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
    border-bottom: 1px solid #eef1f5;
`

const CustomerAvatar = styled.div`
    width: 52px;
    height: 52px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 1rem;
    background: ${props => props.$bg || '#10b981'};
    color: #fff;
`

const CustomerTitleBlock = styled.div`
    flex: 1;
    min-width: 180px;
`

const CustomerName = styled.h2`
    margin: 0;
    font-size: 1.15rem;
    color: #1a2b4b;
`

const CustomerCountry = styled.span`
    font-size: 0.75rem;
    color: #6b7280;
    margin-left: 0.35rem;
`

const SourceBadge = styled.span`
    display: inline-block;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    background: ${props => props.$bg || '#d1fae5'};
    color: ${props => props.$color || '#065f46'};
`

const DetailGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 1rem;
    padding: 1rem 1.25rem;
`

const DetailItem = styled.div``

const DetailLabel = styled.div`
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    color: #9ca3af;
    margin-bottom: 0.25rem;
`

const DetailValue = styled.div`
    font-size: 0.88rem;
    color: #1f2937;
    word-break: break-word;
`

const FormHeader = styled.div`
    background: #fef9c3;
    padding: 0.55rem 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    color: #1a2b4b;
    border-bottom: 1px solid #fde68a;
`

const FormBody = styled.div`
    padding: 1rem 1.25rem;
    width: 100%;
    box-sizing: border-box;
    overflow: visible;
`

const ComplaintForm = styled(Form)`
    &&& {
        width: 100%;
    }

    label {
        font-size: 0.62rem !important;
        font-weight: 700 !important;
        letter-spacing: 0.06em;
        color: #6b7280 !important;
        text-transform: uppercase;
    }

    input,
    textarea,
    .ui.dropdown {
        font-size: 0.85rem !important;
    }

    input:disabled {
        background: #f0f2f5 !important;
        color: #9ca3af !important;
        opacity: 1 !important;
    }

    .ui.calendar.popup,
    .ui.popup.visible {
        z-index: 2000 !important;
    }
`

const SeverityFieldLabel = styled.label`
    display: block;
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    color: #6b7280;
    margin-bottom: 0.35rem;
    text-transform: uppercase;
`

const SeverityRow = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0.5rem;
    margin-top: 0.35rem;
    width: 100%;

    @media (max-width: 600px) {
        grid-template-columns: repeat(2, 1fr);
    }
`

const SeverityBtn = styled.button`
    border: 1px solid ${props => (props.$active ? props.$color : '#d1d5db')};
    background: ${props => (props.$active ? `${props.$color}18` : '#fff')};
    color: #374151;
    border-radius: 6px;
    padding: 0.35rem 0.5rem;
    min-height: ${FIELD_HEIGHT};
    height: ${FIELD_HEIGHT};
    font-size: 0.8rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    font-weight: ${props => (props.$active ? 600 : 400)};
    width: 100%;
    box-sizing: border-box;

    &:hover {
        border-color: ${props => props.$color};
    }
`

const SeverityDot = styled.span`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${props => props.$color};
`

const FormFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.75rem;
    padding: 0.75rem 1.25rem;
    border-top: 1px solid #eef1f5;
    background: #fafbfc;
`

const FooterHint = styled.span`
    font-size: 0.8rem;
    color: #6b7280;
`

const FooterActions = styled.div`
    display: flex;
    gap: 0.5rem;
`

const BtnReset = styled.button`
    border: 1px solid #d1d5db;
    background: #fff;
    color: #374151;
    border-radius: 6px;
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.35rem;

    &:hover {
        background: #f9fafb;
    }
`

const BtnSubmit = styled.button`
    border: none;
    background: ${props => (props.$disabled ? '#9ca3af' : '#1a2b4b')};
    color: #fff;
    border-radius: 6px;
    padding: 0.5rem 1.1rem;
    font-size: 0.85rem;
    cursor: ${props => (props.$disabled ? 'not-allowed' : 'pointer')};
    display: flex;
    align-items: center;
    gap: 0.35rem;
    opacity: ${props => (props.$disabled ? 0.7 : 1)};

    &:hover {
        background: ${props => (props.$disabled ? '#9ca3af' : '#243b6b')};
    }
`

const LogHeader = styled.button`
    width: 100%;
    border: none;
    background: #f4f7f9;
    padding: 0.65rem 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    color: #1a2b4b;
`

const LogTitle = styled.span`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`

const LogBadge = styled.span`
    background: #1a2b4b;
    color: #fff;
    border-radius: 50%;
    min-width: 22px;
    height: 22px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.7rem;
    padding: 0 0.35rem;
`

const LogTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 0.82rem;

    th {
        background: #f4f7f9;
        text-align: left;
        padding: 0.55rem 0.75rem;
        font-size: 0.62rem;
        font-weight: 700;
        letter-spacing: 0.05em;
        color: #1a2b4b;
        border-bottom: 1px solid #e5e9ef;
    }

    td {
        padding: 0.6rem 0.75rem;
        border-bottom: 1px solid #eef1f5;
        color: #374151;
        vertical-align: top;
    }

    tr:hover td {
        background: #fafbfc;
    }
`

const EmptyLog = styled.div`
    padding: 1.5rem;
    text-align: center;
    color: #9ca3af;
    font-size: 0.88rem;
`

const SeverityPill = styled.span`
    display: inline-block;
    padding: 0.15rem 0.45rem;
    border-radius: 4px;
    font-size: 0.72rem;
    font-weight: 600;
    background: ${props => `${props.$color}22`};
    color: ${props => props.$color};
`

const TAB_STYLES = {
    cintas: { activeColor: '#be185d', underlineColor: '#e11d8f', selectedBg: '#1a2b4b' },
    international: { activeColor: '#1d4ed8', underlineColor: '#e11d8f', selectedBg: '#1e40af' },
    our: { activeColor: '#059669', underlineColor: '#10b981', selectedBg: '#047857' },
}

const AVATAR_COLORS = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#6366f1']

const getAvatarColor = index => AVATAR_COLORS[index % AVATAR_COLORS.length]

const startOfToday = () => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return today
}

const normalizeFormDate = value => {
    if (!value) return startOfToday()
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        const d = new Date(value)
        d.setHours(0, 0, 0, 0)
        return d
    }
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) return startOfToday()
    parsed.setHours(0, 0, 0, 0)
    return parsed
}

const getDefaultFormValues = (customerName = '') => ({
    customerName,
    complaintDate: startOfToday(),
    product: '',
    complaintType: '',
    description: '',
})

export const CustomerComplaintLog = ({ inspectionConfig = null }) => {
    const isInspectionMode = !!inspectionConfig
    const {
        auditName,
        initialCustomerId,
        claimRows = [],
        activeRowIndex = 0,
        onRegisterDraftFlush,
        onLogComplaint,
        readOnly = false,
        onSaveAndClose,
        onSubmitInspection,
        onReview,
        onBack,
        showValidation = false,
        fieldErrors = {},
        saving = false,
    } = inspectionConfig || {}

    const { successToast } = useNotification()
    const { data: internalCustomers, loading: internalLoading } = GetCustomerList({
        PageSize: 5000,
        CallFrom: 1,
    })
    const { data: externalCustomers, loading: externalLoading } = GetExternalCustomerList()

    const [activeSource, setActiveSource] = useState('cintas')
    const [searchText, setSearchText] = useState('')
    const [selectedCustomer, setSelectedCustomer] = useState(null)
    const [severity, setSeverity] = useState('Low')
    const [sessionComplaints, setSessionComplaints] = useState([])
    const [logExpanded, setLogExpanded] = useState(true)
    const initialCustomerAppliedRef = useRef(false)
    const lastSyncedSectionRef = useRef(null)
    const selectedCustomerRef = useRef(null)
    const severityRef = useRef('Low')

    const activeRow = claimRows[activeRowIndex] || claimRows[0] || null

    const { control, reset, watch } = useForm({
        defaultValues: getDefaultFormValues(),
    })

    const product = watch('product')
    const complaintType = watch('complaintType')
    const complaintDate = watch('complaintDate')

    const loggedComplaints = useMemo(() => {
        if (!isInspectionMode) return sessionComplaints
        return (claimRows || [])
            .filter(isClaimRowFilled)
            .map((row, idx) => ({
                id: row.sectionId || `claim-${idx}`,
                complaintDate:
                    row.complaintDateDisplay ||
                    formatComplaintDate(row.complaintDate),
                customerName: row.customerName,
                source: row.source,
                product: row.product,
                complaintType: row.complaintType,
                severity: row.severity,
                description: row.description,
            }))
    }, [isInspectionMode, claimRows, sessionComplaints])

    selectedCustomerRef.current = selectedCustomer
    severityRef.current = severity

    useEffect(() => {
        if (!isInspectionMode || !activeRow) return

        const sectionKey = String(activeRow.sectionId || `row-${activeRowIndex}`)
        if (lastSyncedSectionRef.current === sectionKey) return
        lastSyncedSectionRef.current = sectionKey

        reset({
            customerName: activeRow.customerName || '',
            complaintDate: normalizeFormDate(activeRow.complaintDate),
            product: activeRow.product || '',
            complaintType: activeRow.complaintType || '',
            description: activeRow.description || '',
        })
        setSeverity(activeRow.severity || 'Low')
    }, [isInspectionMode, activeRowIndex, activeRow?.sectionId, reset])

    useEffect(() => {
        if (!isInspectionMode || !onRegisterDraftFlush) {
            return () => {}
        }

        onRegisterDraftFlush(() => {
            const values = watch()
            const customer = selectedCustomerRef.current
            const currentSeverity = severityRef.current
            return {
                customerName: customer?.name || '',
                customerId: customer?.id || null,
                source:
                    SOURCE_BADGE_LABELS[customer?.source] ||
                    customer?.source ||
                    '',
                product: values.product || '',
                complaintType: values.complaintType || '',
                severity: currentSeverity || 'Low',
                description: values.description || '',
                complaintDate: normalizeFormDate(values.complaintDate),
                complaintDateDisplay: formatComplaintDate(
                    normalizeFormDate(values.complaintDate),
                ),
            }
        })

        return () => {
            onRegisterDraftFlush(null)
        }
    }, [isInspectionMode, onRegisterDraftFlush])

    const resetComplaintForm = (customerName = '') => {
        reset(getDefaultFormValues(customerName))
        setSeverity('Low')
    }

    const customerLists = useMemo(
        () => buildCustomerLists(internalCustomers, externalCustomers),
        [internalCustomers, externalCustomers],
    )

    const activeList = customerLists[activeSource] || []
    const filteredList = useMemo(
        () => filterCustomers(activeList, searchText),
        [activeList, searchText],
    )

    useEffect(() => {
        if (
            !isInspectionMode ||
            !initialCustomerId ||
            initialCustomerAppliedRef.current
        ) {
            return
        }

        const hasCustomers =
            (customerLists.cintas || []).length > 0 ||
            (customerLists.international || []).length > 0 ||
            (customerLists.our || []).length > 0
        if (!hasCustomers) return

        const all = [
            ...(customerLists.cintas || []),
            ...(customerLists.international || []),
            ...(customerLists.our || []),
        ]
        const match = all.find(c => String(c.id) === String(initialCustomerId))
        if (match) {
            initialCustomerAppliedRef.current = true
            setSelectedCustomer(match)
            setActiveSource(match.source)
            reset(getDefaultFormValues(match.name))
            setSeverity('Low')
        }
    }, [isInspectionMode, initialCustomerId, customerLists, reset])

    const stats = useMemo(() => {
        const total = loggedComplaints.length
        const highCritical = loggedComplaints.filter(c =>
            isHighSeverity(c.severity),
        ).length
        const todayCount = loggedComplaints.filter(c =>
            isToday(c.complaintDate),
        ).length
        return { total, highCritical, todayCount }
    }, [loggedComplaints])

    const tabStyle = TAB_STYLES[activeSource] || TAB_STYLES.cintas

    const canSubmit =
        selectedCustomer && product && complaintType && complaintDate && severity

    const handleSourceChange = key => {
        setActiveSource(key)
        setSearchText('')
        setSelectedCustomer(null)
        lastSyncedSectionRef.current = null
        resetComplaintForm()
    }

    const handleSelectCustomer = customer => {
        setSelectedCustomer(customer)
        resetComplaintForm(customer.name)
    }

    const handleReset = () => {
        resetComplaintForm(selectedCustomer?.name || '')
    }

    const handleSubmit = async () => {
        if (!canSubmit || readOnly) return

        const values = watch()

        if (isInspectionMode && onLogComplaint) {
            await onLogComplaint({
                customerName: selectedCustomer.name,
                customerId: selectedCustomer.id,
                source:
                    SOURCE_BADGE_LABELS[selectedCustomer.source] ||
                    selectedCustomer.source,
                product: values.product,
                complaintType: values.complaintType,
                severity,
                description: values.description,
                complaintDate: values.complaintDate,
                complaintDateDisplay: formatComplaintDate(values.complaintDate),
            })
            resetComplaintForm(selectedCustomer.name)
            successToast('Complaint logged successfully')
            return
        }

        const entry = {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            complaintDate: formatComplaintDate(values.complaintDate),
            customerName: selectedCustomer.name,
            source: SOURCE_BADGE_LABELS[selectedCustomer.source] || selectedCustomer.source,
            product: values.product,
            complaintType: values.complaintType,
            severity,
            description: values.description,
            customerId: selectedCustomer.id,
        }

        setSessionComplaints(prev => [entry, ...prev])
        resetComplaintForm(selectedCustomer.name)
        successToast('Complaint logged successfully')
    }

    const loading = internalLoading || externalLoading

    const footerHint = selectedCustomer
        ? `${selectedCustomer.name} selected — fill the form and submit`
        : '← Select a customer to enable submission'

    return (
        <PageWrap>
            {loading && <LoaderBar content="Loading customers..." />}
            <TopHeader>
                <HeaderLeft>
                    <Warning size={22} weight="fill" color="#fbbf24" />
                    <div>
                        <HeaderTitle>
                            {isInspectionMode && auditName
                                ? auditName
                                : 'Customer Complaint Log'}
                        </HeaderTitle>
                        <HeaderSubtitle>
                            {isInspectionMode
                                ? 'Customer Service Claim Portal — log complaints for this inspection'
                                : 'Select source • Pick customer • Fill complaint • Submit'}
                        </HeaderSubtitle>
                    </div>
                </HeaderLeft>
                <StatsRow>
                    <StatBox>
                        <StatValue>{stats.total}</StatValue>
                        <StatLabel>TOTAL LOGGED</StatLabel>
                    </StatBox>
                    <StatBox>
                        <StatValue $alwaysRed>{stats.highCritical}</StatValue>
                        <StatLabel>HIGH / CRITICAL</StatLabel>
                    </StatBox>
                    <StatBox>
                        <StatValue>{stats.todayCount}</StatValue>
                        <StatLabel>TODAY</StatLabel>
                    </StatBox>
                </StatsRow>
            </TopHeader>

            <BodyLayout>
                <Sidebar>
                    <SourceTabs>
                        {Object.values(SOURCE_TABS).map(tab => (
                            <SourceTab
                                key={tab.key}
                                type="button"
                                $active={activeSource === tab.key}
                                $activeColor={TAB_STYLES[tab.key]?.activeColor}
                                $underlineColor={TAB_STYLES[tab.key]?.underlineColor}
                                onClick={() => handleSourceChange(tab.key)}
                            >
                                <span>{tab.icon}</span>
                                {tab.label}
                                <TabCount>
                                    {(customerLists[tab.key] || []).length}
                                </TabCount>
                            </SourceTab>
                        ))}
                    </SourceTabs>
                    <SearchWrap>
                        <SearchIcon>
                            <MagnifyingGlass size={16} />
                        </SearchIcon>
                        <Input
                            fluid
                            placeholder="Search name, city or state..."
                            value={searchText}
                            onChange={(_, { value }) => setSearchText(value)}
                        />
                    </SearchWrap>
                    <ListMeta>
                        {filteredList.length} LOCATIONS SHOWN
                    </ListMeta>
                    <LocationList>
                        {filteredList.map((customer, index) => {
                            const selected = selectedCustomer?.id === customer.id
                            return (
                                <LocationItem
                                    key={`${customer.source}-${customer.id}`}
                                    type="button"
                                    $selected={selected}
                                    $selectedBg={tabStyle.selectedBg}
                                    onClick={() => handleSelectCustomer(customer)}
                                >
                                    <Avatar
                                        $selected={selected}
                                        $bg={getAvatarColor(index)}
                                    >
                                        {getInitials(customer.name)}
                                    </Avatar>
                                    <div>
                                        <LocationName>{customer.name}</LocationName>
                                        <LocationSub $selected={selected}>
                                            {formatLocationLine(customer)}
                                        </LocationSub>
                                    </div>
                                </LocationItem>
                            )
                        })}
                        {!loading && filteredList.length === 0 && (
                            <EmptyLog>No locations found</EmptyLog>
                        )}
                    </LocationList>
                </Sidebar>

                <MainArea>
                    {!selectedCustomer ? (
                        <PlaceholderCard>
                            <PlaceholderInner>
                                <PlaceholderIconWrap>
                                    <MapPin size={36} weight="regular" />
                                </PlaceholderIconWrap>
                                <PlaceholderText>
                                    Select a customer from the left panel to view their
                                    address &amp; details here
                                </PlaceholderText>
                            </PlaceholderInner>
                        </PlaceholderCard>
                    ) : (
                        <Card>
                            <CustomerHeader>
                                <CustomerAvatar $bg={tabStyle.selectedBg}>
                                    {getInitials(selectedCustomer.name)}
                                </CustomerAvatar>
                                <CustomerTitleBlock>
                                    <CustomerName>
                                        {selectedCustomer.name}
                                        <CustomerCountry>
                                            {selectedCustomer.country || 'US'}
                                        </CustomerCountry>
                                    </CustomerName>
                                    <SourceBadge
                                        $bg={
                                            selectedCustomer.source === 'our'
                                                ? '#d1fae5'
                                                : '#dbeafe'
                                        }
                                        $color={
                                            selectedCustomer.source === 'our'
                                                ? '#065f46'
                                                : '#1e40af'
                                        }
                                    >
                                        {SOURCE_BADGE_LABELS[selectedCustomer.source]}
                                    </SourceBadge>
                                </CustomerTitleBlock>
                            </CustomerHeader>
                            <DetailGrid>
                                <DetailItem>
                                    <DetailLabel>CONTACT</DetailLabel>
                                    <DetailValue>{selectedCustomer.contact}</DetailValue>
                                </DetailItem>
                                <DetailItem>
                                    <DetailLabel>ADDRESS</DetailLabel>
                                    <DetailValue>
                                        {selectedCustomer.address || '-'}
                                    </DetailValue>
                                </DetailItem>
                                <DetailItem>
                                    <DetailLabel>CITY / STATE / ZIP</DetailLabel>
                                    <DetailValue>
                                        {[selectedCustomer.city, selectedCustomer.state, selectedCustomer.zip]
                                            .filter(Boolean)
                                            .join(', ') || '-'}
                                    </DetailValue>
                                </DetailItem>
                                <DetailItem>
                                    <DetailLabel>PHONE</DetailLabel>
                                    <DetailValue>{selectedCustomer.phone}</DetailValue>
                                </DetailItem>
                                <DetailItem>
                                    <DetailLabel>EMAIL</DetailLabel>
                                    <DetailValue>{selectedCustomer.email}</DetailValue>
                                </DetailItem>
                            </DetailGrid>
                        </Card>
                    )}

                    <FormCard>
                        <FormHeader>
                            <PencilSimple size={18} weight="bold" />
                            LOG A COMPLAINT
                        </FormHeader>
                        <FormBody>
                            <ComplaintForm noValidate>
                                <Form.Group widths="equal">
                                    <FormTextFormField
                                        control={control}
                                        name="customerName"
                                        label="Customer (Auto-Filled)"
                                        placeholder="Select a customer first..."
                                        disabled
                                        readOnly
                                    />
                                    <FormDateField
                                        key={`complaint-date-${isInspectionMode ? `${activeRowIndex}-${activeRow?.sectionId || 'new'}` : 'standalone'}`}
                                        control={control}
                                        name="complaintDate"
                                        label="Date of Complaint"
                                        type="basic"
                                        position="bottom right"
                                        placeholder="Select date"
                                    />
                                </Form.Group>
                                <Form.Group widths="equal">
                                    <FormDropdownField
                                        control={control}
                                        name="product"
                                        label="Product"
                                        placeholder="— Select product —"
                                        options={PRODUCT_OPTIONS}
                                    />
                                    <FormDropdownField
                                        control={control}
                                        name="complaintType"
                                        label="Complaint Type"
                                        placeholder="— Select type —"
                                        options={COMPLAINT_TYPE_OPTIONS}
                                    />
                                </Form.Group>
                                <div style={{ marginBottom: '1rem' }}>
                                    <SeverityFieldLabel>Severity</SeverityFieldLabel>
                                    <SeverityRow>
                                        {SEVERITY_OPTIONS.map(opt => (
                                            <SeverityBtn
                                                key={opt.key}
                                                type="button"
                                                $active={severity === opt.value}
                                                $color={opt.color}
                                                onClick={() => setSeverity(opt.value)}
                                            >
                                                {opt.key === 'critical' ? (
                                                    <Warning size={14} weight="fill" />
                                                ) : (
                                                    <SeverityDot $color={opt.color} />
                                                )}
                                                {opt.label}
                                            </SeverityBtn>
                                        ))}
                                    </SeverityRow>
                                </div>
                                <FormTextAreaFormField
                                    control={control}
                                    name="description"
                                    label="Description / Notes"
                                    placeholder="Describe the issue in detail..."
                                    rows={4}
                                />
                            </ComplaintForm>
                        </FormBody>
                        <FormFooter>
                            <FooterHint>{footerHint}</FooterHint>
                            <FooterActions>
                                <BtnReset type="button" onClick={handleReset}>
                                    <ArrowCounterClockwise size={18} />
                                    Reset
                                </BtnReset>
                                <BtnSubmit
                                    type="button"
                                    $disabled={!canSubmit || readOnly}
                                    onClick={handleSubmit}
                                >
                                    <Check size={18} weight="bold" />
                                    Log Complaint
                                </BtnSubmit>
                            </FooterActions>
                        </FormFooter>
                    </FormCard>

                    <Card>
                        <LogHeader
                            type="button"
                            onClick={() => setLogExpanded(v => !v)}
                        >
                            <LogTitle>
                                <ClipboardText size={18} />
                                COMPLAINT LOG
                                <LogBadge>{loggedComplaints.length}</LogBadge>
                            </LogTitle>
                            {logExpanded ? <CaretUp size={16} /> : <CaretDown size={16} />}
                        </LogHeader>
                        {logExpanded && (
                            loggedComplaints.length === 0 ? (
                                <EmptyLog>
                                    No complaints logged yet this session.
                                </EmptyLog>
                            ) : (
                                <div style={{ overflowX: 'auto' }}>
                                    <LogTable>
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>DATE</th>
                                                <th>CUSTOMER</th>
                                                <th>SOURCE</th>
                                                <th>PRODUCT</th>
                                                <th>TYPE</th>
                                                <th>SEVERITY</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loggedComplaints.map((row, idx) => {
                                                const sev = SEVERITY_OPTIONS.find(
                                                    s => s.value === row.severity,
                                                )
                                                return (
                                                    <tr key={row.id}>
                                                        <td>{loggedComplaints.length - idx}</td>
                                                        <td>{row.complaintDate}</td>
                                                        <td>{row.customerName}</td>
                                                        <td>{row.source}</td>
                                                        <td>{row.product}</td>
                                                        <td>{row.complaintType}</td>
                                                        <td>
                                                            <SeverityPill
                                                                $color={
                                                                    sev?.color || '#6b7280'
                                                                }
                                                            >
                                                                {row.severity}
                                                            </SeverityPill>
                                                        </td>
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </LogTable>
                                </div>
                            )
                        )}
                    </Card>
                </MainArea>
            </BodyLayout>

            {isInspectionMode && (
                <div
                    style={{
                        padding: '1rem',
                        background: '#fff',
                        borderTop: '1px solid #e2e8f0',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '0.75rem',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    <div>
                        {showValidation && fieldErrors.__customer_claim_customer__ && (
                            <span style={{ color: '#dc2626', fontSize: '0.85rem' }}>
                                {fieldErrors.__customer_claim_customer__.message}
                            </span>
                        )}
                        {showValidation && fieldErrors.__customer_claim_entries__ && (
                            <span style={{ color: '#dc2626', fontSize: '0.85rem' }}>
                                {fieldErrors.__customer_claim_entries__.message}
                            </span>
                        )}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {onBack && (
                            <AppSecondaryButton type="button" onClick={onBack}>
                                Back
                            </AppSecondaryButton>
                        )}
                        {onSaveAndClose && (
                            <AppSecondaryButton
                                type="button"
                                onClick={onSaveAndClose}
                                disabled={readOnly || saving}
                            >
                                Save &amp; Close
                            </AppSecondaryButton>
                        )}
                        {onReview && (
                            <AppPrimaryButton
                                type="button"
                                onClick={onReview}
                                disabled={readOnly || saving}
                            >
                                Review
                            </AppPrimaryButton>
                        )}
                        {onSubmitInspection && (
                            <AppPrimaryButton
                                type="button"
                                onClick={onSubmitInspection}
                                disabled={readOnly || saving}
                            >
                                Submit
                            </AppPrimaryButton>
                        )}
                    </div>
                </div>
            )}
        </PageWrap>
    )
}